/**
 * TierBadge (T-036).
 *
 * Reflects the subscriber's tier in the portal top bar. Studio and Pro are
 * the only two tiers (enums.tierSchema). When an operator views the portal
 * there is no subscriber row, so the badge shows an "Operator view" marker
 * instead of inventing a tier.
 *
 * Visual contract: matches the prototype `Tag` atom. The tier badge uses the
 * navy variant (navy fill, linen text, 2px radius, uppercase, 0.05em tracking)
 * so it reads the same on the navy sidebar as in the top bar. The operator
 * marker uses the outline variant. Pro still gets a glanceable accent dot. No
 * terracotta here — terracotta is reserved for the learning-layer signal
 * (DES-05).
 */

import { Badge } from '@/components/ui/badge';

export interface TierBadgeProps {
  tier: 'studio' | 'pro' | null;
  isOperatorView?: boolean;
}

const TAG_BASE =
  'rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em]';

export function TierBadge({ tier, isOperatorView = false }: TierBadgeProps) {
  if (isOperatorView || tier === null) {
    return (
      <Badge
        variant="outline"
        className={`${TAG_BASE} border-[color:var(--color-navy)] bg-transparent text-[color:var(--color-navy)]`}
      >
        Operator view
      </Badge>
    );
  }

  const isPro = tier === 'pro';
  return (
    <Badge
      variant="outline"
      className={`${TAG_BASE} border-transparent bg-[color:var(--color-navy)] text-[color:var(--color-linen)]`}
      aria-label={`Subscription tier: ${isPro ? 'Pro' : 'Studio'}`}
    >
      {isPro ? (
        <span
          className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-rose)]"
          aria-hidden="true"
        />
      ) : null}
      {isPro ? 'Pro' : 'Studio'}
    </Badge>
  );
}
